import type { FC, KeyboardEvent } from 'react';
import React, { useMemo, useState } from 'react';
import {
  Box,
  InputBase,
  MenuItem,
  MenuList,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Iconify } from '../../Iconify';
import type { SelectOption } from '../declarations/type';

// Short lists are faster to scan than to type into.
const SEARCH_MIN_OPTIONS = 7;
// About ten rows; the search field stays in place while the list scrolls.
const LIST_MAX_HEIGHT = 320;

interface SelectMenuProps {
  options: SelectOption[];
  value: string | undefined;
  onChange: (id: string) => void;
}

/**
 * The options of a select filter as a plain menu, for the filter pill popover.
 * Long lists get a search field on top; Enter picks the first match.
 *
 * @example
 * <SelectMenu options={cashLists} value={cashListId} onChange={setCashListId} />
 */
export const SelectMenu: FC<SelectMenuProps> = ({
  options,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const searchable = options.length >= SEARCH_MIN_OPTIONS;

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle
      ? options.filter((option) => option.label.toLowerCase().includes(needle))
      : options;
  }, [options, query]);

  const onSearchKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && matches[0]) {
      onChange(matches[0].id);
    }
  };

  return (
    <Box sx={{ width: 280 }}>
      {searchable && (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1,
            height: 36,
            px: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Iconify
            icon="eva:search-fill"
            width={16}
            sx={{ color: 'text.disabled' }}
          />
          <InputBase
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={t('table.filters.search')}
            sx={{ flex: 1, fontSize: 14, color: 'primary.main' }}
          />
        </Stack>
      )}
      <MenuList
        disablePadding
        sx={{
          maxHeight: LIST_MAX_HEIGHT,
          overflowX: 'hidden',
          overflowY: 'auto',
          p: 0.5,
        }}
      >
        {matches.map((option) => {
          const selected = option.id === value;
          return (
            <MenuItem
              key={option.id}
              selected={selected}
              onClick={() => onChange(option.id)}
              sx={{
                gap: 1,
                minHeight: 'auto',
                px: 1,
                py: 0.75,
                borderRadius: 1,
                fontSize: 14,
                color: 'primary.main',
                '&:hover': { bgcolor: 'background.neutral' },
                '&.Mui-selected, &.Mui-selected:hover': {
                  bgcolor: 'primary.500_8',
                },
              }}
            >
              <Box
                component="span"
                title={option.label}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.label}
              </Box>
              {/* Reserved so labels truncate at the same width whether selected or not. */}
              <Box sx={{ width: 16, flexShrink: 0, display: 'flex' }}>
                {selected && <Iconify icon="eva:checkmark-fill" width={16} />}
              </Box>
            </MenuItem>
          );
        })}
      </MenuList>
      {matches.length === 0 && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            px: 2.25,
            pb: 2,
          }}
        >
          {t('table.filters.no-match')}
        </Typography>
      )}
    </Box>
  );
};
